import { RadioGroup, RadioGroupProps, Stack, StackDirection } from '@chakra-ui/react';
import {
  FieldHelpText,
  FieldTitle,
  InputVariant,
  Radio,
  SelectOption,
} from '@pluxee-design-system/core';
import ProductCard from 'common/cards/ProductCard';
import { getCheckboxGroupVariant } from 'common/forms/CheckboxGroupControl';
import { ComponentType, useCallback } from 'react';
import { useController } from 'react-hook-form';

export interface ProductGroupControlProps extends Omit<
  RadioGroupProps,
  'isDisabled' | 'name' | 'children'
> {
  direction?: StackDirection;
  disabled?: boolean;
  mandatory?: boolean;
  name: string;
  readonly?: boolean;
  renderLeftElement?: ComponentType<SelectOption>;
  title?: string;
  options: readonly SelectOption[];
}

const ProductGroupControl = ({
  direction,
  disabled,
  id,
  mandatory,
  name,
  onChange: rawOnChange,
  options,
  readonly,
  renderLeftElement: RenderLeftElement,
  title,
}: ProductGroupControlProps) => {
  const { field, fieldState } = useController({ name });
  const { onChange } = field;
  const { error, isTouched } = fieldState;
  const hasError = Boolean(error);
  const variant = getCheckboxGroupVariant(readonly, isTouched, hasError, disabled);

  const handleRadioChange = useCallback(
    (value: string) => {
      onChange([value]);
    },
    [onChange],
  );

  return (
    <>
      {title && (
        <FieldTitle
          title={title}
          mandatory={Boolean(mandatory)}
          disabled={variant === 'disabled'}
          variant={variant !== 'disabled' ? variant : undefined}
        />
      )}
      <RadioGroup
        id={id}
        isDisabled={variant === 'disabled'}
        value={field.value?.[0] ?? ''}
        name={name}
        onChange={rawOnChange ?? handleRadioChange}
      >
        <Stack align="stretch" direction={direction} spacing={4}>
          {options.map((option) => (
            <ProductCard
              key={option.value}
              variant={
                (variant === InputVariant.FILLED || variant === InputVariant.SUCCESS) &&
                field.value.includes(option.value)
                  ? 'selected'
                  : variant
              }
            >
              <Radio key={option.value} value={option.value}>
                {RenderLeftElement && <RenderLeftElement {...option} />}
                {option.label}
              </Radio>
            </ProductCard>
          ))}
        </Stack>
      </RadioGroup>
      {hasError && error?.message && (
        <FieldHelpText
          disabled={variant === 'disabled'}
          text={error.message}
          variant={InputVariant.ERROR}
        />
      )}
    </>
  );
};

export default ProductGroupControl;
